// The customer's Settings tab: prompt logging and retention (GET/PATCH /v1/orgs/{orgId}/settings,
// API-012), and, for a super-admin, the customer's Assistant switch (`assistantEnabled`, REQ-110).
// Cost visibility can be edited here when `billing:write` (`PUT
// /v1/admin/orgs/{orgId}/billing-settings/cost-visibility` — REQ-013, REQ-050).
import { useContext, useEffect, useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import { ApiError } from "@nexus/ui";
import { SessionContext } from "../../session/session";
import { Checkbox, errorId, Field, hintId } from "@nexus/ui";
import { hasErrors, type FieldErrors } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ErrorBanner, Loading } from "@nexus/ui";
import { COST_VISIBILITY_LABELS, COST_VISIBILITY_OPTIONS } from "../terms/model";
import {
  canEditCostVisibility,
  canSwitchAssistant,
  canViewCostVisibility,
  patchOrgSettingsRequest,
  putCostVisibilityRequest,
  validateOrgSettingsForm,
  type CostVisibility,
  type OrgSettings,
  type OrgSettingsValues,
  type PatchOrgSettingsRequest,
} from "./model";

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

export function Settings({ orgId }: { orgId: string }) {
  const client = useApi();
  const services = useContext(SessionContext);
  const session = services?.state.status === "signed-in" ? services.state.session : null;

  const [settings, setSettings] = useState<OrgSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [form, setForm] = useState<OrgSettingsValues>({ promptRetentionDays: "", usageRetentionDays: "" });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [costVisibilityBusy, setCostVisibilityBusy] = useState(false);
  const [costVisibilitySaved, setCostVisibilitySaved] = useState(false);
  const [costVisibilityError, setCostVisibilityError] = useState<ApiError | null>(null);

  const load = () => {
    setLoading(true);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/settings", { path: { orgId } })
      .then((data) => {
        setSettings(data);
        setForm({ promptRetentionDays: String(data.promptRetentionDays), usageRetentionDays: String(data.usageRetentionDays) });
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client, orgId]);

  const save = () => {
    if (!settings) return;
    const validation = validateOrgSettingsForm(form);
    if (hasErrors(validation)) {
      setErrors(validation);
      return;
    }
    const body = patchOrgSettingsRequest(settings.promptLoggingEnabled, form);
    if (!body) return;
    setSaving(true);
    setSaved(false);
    client
      .patch("/v1/orgs/{orgId}/settings", { path: { orgId }, body })
      .then((data) => {
        setSettings(data);
        setErrors({});
        setSaved(true);
      })
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setSaving(false));
  };

  /** Patch one switch: the body names only it (`assistantEnabled` is refused from all but a super-admin). */
  const patchSwitch = (body: PatchOrgSettingsRequest) => {
    setSaving(true);
    client
      .patch("/v1/orgs/{orgId}/settings", { path: { orgId }, body })
      .then((data) => setSettings(data))
      .catch((err: unknown) => setError(unexpected(err)))
      .finally(() => setSaving(false));
  };

  const togglePromptLogging = () => settings && patchSwitch({ promptLoggingEnabled: !settings.promptLoggingEnabled });
  const toggleAssistant = () => settings && patchSwitch({ assistantEnabled: !settings.assistantEnabled });

  const changeCostVisibility = (next: CostVisibility) => {
    if (!settings || costVisibilityBusy || next === settings.costVisibility) return;
    setCostVisibilityBusy(true);
    setCostVisibilityError(null);
    setCostVisibilitySaved(false);
    client
      .put("/v1/admin/orgs/{orgId}/billing-settings/cost-visibility", { path: { orgId }, body: putCostVisibilityRequest(next) })
      .then((updated) => {
        setSettings({ ...settings, costVisibility: updated.costVisibility });
        setCostVisibilitySaved(true);
      })
      .catch((err: unknown) => setCostVisibilityError(unexpected(err)))
      .finally(() => setCostVisibilityBusy(false));
  };

  if (loading) return <Loading label="Loading settings…" />;
  if (error) return <ErrorBanner error={error} onRetry={load} />;
  if (!settings) return null;

  return (
    <div class="split">
      <Card title="Prompt logging and retention" help="customers.retention">
        <Checkbox
          id="prompt-logging"
          label="Prompt logging enabled"
          help="customers.promptLogging"
          checked={settings.promptLoggingEnabled}
          disabled={saving}
          onChange={togglePromptLogging}
        />
        <div class="form-grid">
          <Field
            id="prompt-retention"
            label="Prompt retention (days)"
            help="customers.promptRetention"
            error={errors.promptRetentionDays}
            hint="1 to 3,650 days."
            class="field-narrow"
          >
            <input
              id="prompt-retention"
              inputMode="numeric"
              value={form.promptRetentionDays}
              aria-invalid={errors.promptRetentionDays ? true : undefined}
              aria-describedby={errors.promptRetentionDays ? errorId("prompt-retention") : hintId("prompt-retention")}
              onInput={(event) => setForm({ ...form, promptRetentionDays: event.currentTarget.value })}
            />
          </Field>
          <Field
            id="usage-retention"
            label="Usage retention (days)"
            help="customers.usageRetention"
            error={errors.usageRetentionDays}
            hint="30 to 3,650 days."
            class="field-narrow"
          >
            <input
              id="usage-retention"
              inputMode="numeric"
              value={form.usageRetentionDays}
              aria-invalid={errors.usageRetentionDays ? true : undefined}
              aria-describedby={errors.usageRetentionDays ? errorId("usage-retention") : hintId("usage-retention")}
              onInput={(event) => setForm({ ...form, usageRetentionDays: event.currentTarget.value })}
            />
          </Field>
        </div>
        <div class="form-actions-start">
          <button type="button" class="button button-primary" disabled={saving} onClick={save}>
            {saving ? "Saving…" : "Save"}
          </button>
          {saved && <p role="status">Saved.</p>}
        </div>
      </Card>

      {session && canSwitchAssistant(session) && (
        <Card title="Assistant" help="customers.assistantEnabled">
          <p class="card-note" id="assistant-enabled-note">
            Off hides the Assistant from the customer's admins on their proxy. AllCode's own Assistant conversations about this customer are unaffected.
          </p>
          <Checkbox
            id="assistant-enabled"
            label="Assistant on the customer's proxy"
            help="customers.assistantEnabled"
            checked={settings.assistantEnabled}
            disabled={saving}
            describedBy="assistant-enabled-note"
            onChange={toggleAssistant}
          />
        </Card>
      )}

      {session && canViewCostVisibility(session) && (
        <Card title="Cost visibility" help="terms.costVisibility">
          <p class="card-note">Whether the customer's own admins and service keys see AWS cost. Takes effect at once, for every month.</p>
          {canEditCostVisibility(session) ? (
            <div class="form-grid">
              <Field id="cost-visibility" label="The customer sees" help="terms.costVisibility" class="field-narrow">
                <select
                  id="cost-visibility"
                  value={settings.costVisibility}
                  disabled={costVisibilityBusy}
                  onChange={(event) => changeCostVisibility(event.currentTarget.value as CostVisibility)}
                >
                  {COST_VISIBILITY_OPTIONS.map((option) => (
                    <option key={option} value={option}>
                      {COST_VISIBILITY_LABELS[option]}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
          ) : (
            <p>
              <Chip tone={settings.costVisibility === "hidden" ? "grey" : "green"}>{COST_VISIBILITY_LABELS[settings.costVisibility]}</Chip>
            </p>
          )}
          {costVisibilitySaved && <p class="field-hint" role="status">Saved.</p>}
          {costVisibilityError && <ErrorBanner error={costVisibilityError} />}
        </Card>
      )}
    </div>
  );
}
